import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Role hierarchy
export const ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  SUPER_ADMIN: 'superAdmin'
};

// Role permissions
export const ROLE_PERMISSIONS = {
  [ROLES.USER]: {
    canRecord: true,
    canListen: true,
    canJoinFamilies: true,
    canLeaveFamilies: true,
    canViewOwnRecordings: true,
    canViewFamilyRecordings: true
  },
  [ROLES.ADMIN]: {
    canRecord: true,
    canListen: true,
    canJoinFamilies: true,
    canLeaveFamilies: true,
    canViewOwnRecordings: true,
    canViewFamilyRecordings: true,
    canManageFamilyMembers: true,
    canInviteMembers: true,
    canRemoveMembers: true,
    canManageFamilySettings: true
  },
  [ROLES.SUPER_ADMIN]: {
    canRecord: true,
    canListen: true,
    canJoinFamilies: true,
    canLeaveFamilies: true,
    canViewOwnRecordings: true,
    canViewFamilyRecordings: true,
    canManageFamilyMembers: true,
    canInviteMembers: true,
    canRemoveMembers: true,
    canManageFamilySettings: true,
    canAccessSuperAdmin: true,
    canManageAllUsers: true,
    canManageAllFamilies: true,
    canViewSystemMetrics: true,
    canManageSystemSettings: true
  }
};

// Check if user has a specific permission
export const hasPermission = (userRole, permission) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }
  return ROLE_PERMISSIONS[userRole][permission] || false;
};

// Check if user has a specific role or higher
export const hasRole = (userRole, requiredRole) => {
  const roleHierarchy = [ROLES.USER, ROLES.ADMIN, ROLES.SUPER_ADMIN];
  const userRoleIndex = roleHierarchy.indexOf(userRole);
  const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);
  
  return userRoleIndex >= requiredRoleIndex;
};

// Update user role (only super admins can do this)
export const updateUserRole = async (userId, newRole, currentUserRole) => {
  if (!hasRole(currentUserRole, ROLES.SUPER_ADMIN)) {
    throw new Error('Insufficient permissions to update user roles');
  }
  
  if (!Object.values(ROLES).includes(newRole)) {
    throw new Error('Invalid role specified');
  }
  
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    role: newRole,
    updatedAt: new Date(),
    updatedBy: currentUserRole
  });
  
  return { success: true, message: `User role updated to ${newRole}` };
};

// Get user role
export const getUserRole = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  
  if (userDoc.exists()) {
    return userDoc.data().role || ROLES.USER;
  }
  
  return null;
};

// Check if user is super admin
export const isSuperAdmin = (userData) => {
  return userData?.superAdmin === true;
};

// Check if user is admin or higher
export const isAdmin = (userRole) => {
  return hasRole(userRole, ROLES.ADMIN);
};

// Get role display name
export const getRoleDisplayName = (role) => {
  const displayNames = {
    [ROLES.USER]: 'User',
    [ROLES.ADMIN]: 'Admin',
    [ROLES.SUPER_ADMIN]: 'Super Admin'
  };
  
  return displayNames[role] || 'Unknown';
};

// Get role description
export const getRoleDescription = (role) => {
  const descriptions = {
    [ROLES.USER]: 'Can record and listen to Bible verses, join families',
    [ROLES.ADMIN]: 'Can manage family members and settings',
    [ROLES.SUPER_ADMIN]: 'Full system access and management capabilities'
  };
  
  return descriptions[role] || 'No description available';
};
